import { PiShareNetwork } from 'react-icons/pi';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  MissingPropertySharingLink,
  ResolvedPropertySharingLink,
} from 'src/api/generated';
import Button from '../atoms/buttons/button';
import { Text } from '../atoms/text';
import { useI18n } from '../../hooks/provider-bounded/i18n';
import type { PropertySharingActionProps } from '../../types/catalog/property-sharing.types';

/** Copy exactly the prepared URL and refresh through existing property queries after errors. */
export function PropertySharingAction({
  sharingLink,
}: PropertySharingActionProps) {
  const { t } = useI18n();
  const client = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  useEffect(() => {
    setCopied(false);
    setCopyError(false);
  }, [sharingLink]);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        className="flex items-center gap-2"
        appearance="outline"
        disabled={
          sharingLink?.status !== ResolvedPropertySharingLink.status.RESOLVED
        }
        title={
          !sharingLink
            ? t('propertyLinks.resolutionError')
            : sharingLink.status === MissingPropertySharingLink.status.NONE
              ? t('propertyLinks.noLink')
              : undefined
        }
        onClick={async () => {
          if (
            sharingLink?.status !== ResolvedPropertySharingLink.status.RESOLVED
          ) {
            return;
          }
          try {
            await navigator.clipboard.writeText(sharingLink.url);
            setCopied(true);
            setCopyError(false);
          } catch {
            setCopyError(true);
          }
        }}
      >
        <PiShareNetwork className="h-4 w-4" aria-hidden="true" />
        {copied ? t('propertyLinks.copied') : t('propertyLinks.shareAction')}
      </Button>
      {!sharingLink && (
        <Button
          size="sm"
          appearance="ghost"
          title={t('propertyLinks.resolutionError')}
          onClick={() =>
            void client.invalidateQueries({ queryKey: ['catalog/properties'] })
          }
        >
          {t('propertyLinks.retry')}
        </Button>
      )}
      {copyError && (
        <div role="alert">
          <Text size="small">{t('propertyLinks.copyError')}</Text>
        </div>
      )}
    </div>
  );
}
